import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';

// The sample payload the builder maps from: the parsed sample, its JSON editor,
// and which tree nodes are collapsed.
export function useSamplePayload({
  initialSampleInput,
  onSampleInputChange,
}: {
  initialSampleInput?: object;
  onSampleInputChange?: (sample: object) => void;
}) {
  const { t } = useTranslation();
  const [sampleInput, setSampleInput] = useState<any>(initialSampleInput ?? {});
  const [rawJsonInput, setRawJsonInput] = useState('');
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // ── Sync initial sample input from props ──
  useEffect(() => {
    if (initialSampleInput && Object.keys(initialSampleInput).length > 0) {
      setSampleInput(initialSampleInput);
    }
  }, [initialSampleInput]);

  const toggleCollapsed = useCallback(
    (key: string) =>
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      }),
    [],
  );

  const startEditing = () => {
    setIsEditingJson(true);
    setRawJsonInput(JSON.stringify(sampleInput, null, 2));
  };
  const cancelEditing = () => {
    setIsEditingJson(false);
    setJsonParseError(null);
  };
  const applyJsonEdit = () => {
    try {
      const parsed = JSON.parse(rawJsonInput);
      // The source tree reads keys off the sample: `null` or a bare value would crash it.
      if (parsed === null || typeof parsed !== 'object') {
        setJsonParseError(t('Invalid JSON'));
        return;
      }
      setSampleInput(parsed);
      onSampleInputChange?.(parsed);
      setJsonParseError(null);
      setIsEditingJson(false);
    } catch {
      setJsonParseError(t('Invalid JSON'));
    }
  };

  return {
    sampleInput,
    rawJsonInput,
    setRawJsonInput,
    isEditingJson,
    jsonParseError,
    collapsedNodes,
    toggleCollapsed,
    startEditing,
    cancelEditing,
    applyJsonEdit,
  };
}

export type SamplePayload = ReturnType<typeof useSamplePayload>;
